import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LockClosedIcon,
  CreditCardIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

const CompanySubscriptionGuard = ({ children }) => {
  const { user, apiCall } = useAuth();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState("");

  const getCompanyId = () => {
    return (
      user?.company_id ||
      user?.companyId ||
      user?.company?.id ||
      user?.company?.company_id ||
      ""
    );
  };

  const isSubscriptionActive = (data) => {
    const status = String(
      data?.status ||
        data?.data?.status ||
        data?.subscription_status ||
        data?.data?.subscription_status ||
        "",
    ).toLowerCase();

    const explicitActive = Boolean(
      data?.is_active ??
        data?.data?.is_active ??
        data?.active ??
        data?.data?.active ??
        false,
    );

    const activeStatuses = ["active", "trialing"];

    return explicitActive || activeStatuses.includes(status);
  };

  const loadSubscriptionStatus = async () => {
    const companyId = getCompanyId();

    if (!companyId) {
      setSubscription(null);
      setError("Company ID was not found for this account.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await apiCall(
        `/billing/subscription-status?company_id=${encodeURIComponent(
          companyId,
        )}`,
      );

      setSubscription(response);
    } catch (err) {
      console.error("Subscription guard error:", err);
      setSubscription(null);
      setError(
        err.message || "Unable to check subscription status. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.company_id, user?.companyId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center px-6 font-['Inter']">
        <div className="max-w-md w-full rounded-[36px] border border-slate-100 bg-white p-8 text-center shadow-[0_32px_80px_-24px_rgba(15,23,42,0.18)]">
          <ArrowPathIcon className="mx-auto h-8 w-8 text-[#F65814] animate-spin" />

          <h1 className="mt-6 text-2xl font-[900] uppercase tracking-tight text-[#0A1128]">
            Checking subscription
          </h1>

          <p className="mt-3 text-xs font-bold uppercase tracking-[2px] text-slate-400">
            Please wait...
          </p>
        </div>
      </div>
    );
  }

  if (!isSubscriptionActive(subscription)) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-20 font-['Inter']">
        <div className="max-w-xl w-full rounded-[42px] border border-orange-100 bg-white p-8 md:p-10 text-center shadow-[0_32px_90px_-24px_rgba(246,88,20,0.25)]">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-orange-50">
            <LockClosedIcon className="h-8 w-8 text-[#F65814]" />
          </div>

          <h1 className="mt-8 text-3xl md:text-4xl font-[900] uppercase tracking-tight text-[#0A1128]">
            Subscription required
          </h1>

          <p className="mt-4 text-sm font-bold leading-7 text-slate-500">
            Для доступа в панель компании нужна активная подписка. Выберите план
            и оплатите подписку через Stripe.
          </p>

          {error && (
            <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-left">
              <p className="text-xs font-bold text-red-600">{error}</p>
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/pricing"
              state={{ from: location.pathname }}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-[20px] bg-[#F65814] px-6 py-4 text-[11px] font-black uppercase tracking-[2px] text-white shadow-lg shadow-orange-100 transition-all hover:bg-[#0A1128]"
            >
              <CreditCardIcon className="h-5 w-5" />
              Choose Plan
            </Link>

            <button
              type="button"
              onClick={loadSubscriptionStatus}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-[20px] border border-slate-200 bg-white px-6 py-4 text-[11px] font-black uppercase tracking-[2px] text-[#0A1128] transition-all hover:border-[#F65814] hover:text-[#F65814]"
            >
              Recheck
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default CompanySubscriptionGuard;